// Pure utilities for the credits display layer. Kept dependency-free so
// unit tests don't need to drag in `window` or React.
//
// 1 credit = 10 cents. Backend stays in cents — these helpers are the
// only sanctioned cents→credits conversion in the dashboard.

/** Conversion ratio — 1 credit = 10 cents. */
export const CENTS_PER_CREDIT = 10;

/** Cents → integer credits (floor). Negative cents return 0. */
export function centsToCredits(cents: number): number {
  if (cents <= 0) return 0;
  return Math.floor(cents / CENTS_PER_CREDIT);
}

/**
 * Remaining credits given used vs limit cents. Never negative — the
 * CEO requirement is "don't show negative values to the user". Caller
 * is responsible for rendering "unlimited" when limitCents < 0; this
 * helper returns 0 for that case as a safe default.
 */
export function remainingCredits(usedCents: number, limitCents: number): number {
  if (limitCents < 0) return 0;
  return Math.max(0, centsToCredits(limitCents) - centsToCredits(usedCents));
}

/**
 * Quota dimensions the backend reports in CENTS while every user-facing label
 * calls them credits.
 *
 * `llm_cost_cents_per_month` is 500 for the $5/mo tier -- which the quota table
 * itself annotates as "50 credits @ 10c/credit". The Billing page's credits
 * block converts; its Limits summary read the same row raw, so one page said
 * "3 of 50" and "33 of 500" about the same budget.
 */
const CENTS_DIMENSIONS = new Set(["llm_cost_cents_per_month"]);

export function isCentsDimension(dimension: string): boolean {
  return CENTS_DIMENSIONS.has(dimension);
}

/**
 * A quota row in the units its label claims.
 *
 * A negative limit is the unlimited sentinel and must survive unconverted --
 * centsToCredits(-1) is 0, which would read as a zero ceiling.
 */
export function quotaInLabelledUnits<T extends { dimension: string; limit: number; current: number | null }>(
  quota: T,
): T {
  if (!isCentsDimension(quota.dimension)) return quota;
  return {
    ...quota,
    limit: quota.limit < 0 ? quota.limit : centsToCredits(quota.limit),
    current: quota.current === null ? null : centsToCredits(quota.current),
  };
}
